import { useCallback, useState } from "react";
import {
  listarHorariosPorCurso,
  crearHorario,
  actualizarHorario,
  eliminarHorario,
} from "../../../../services/horariosService";
import { showToast } from "../../../../utils/showToast";

const normalizeHorario = (horario = {}) => ({
  id: horario.id,
  cursoId: horario.curso_id ?? horario.cursoId,
  nombre: horario.nombre || "",
  descripcion: horario.descripcion || "",
  horaInicio: horario.hora_inicio || horario.horaInicio || "",
  horaFin: horario.hora_fin || horario.horaFin || "",
  diasSemana: horario.dias_semana || horario.diasSemana || "",
  activo: horario.activo !== undefined ? horario.activo : true,
});

export default function useHorarios() {
  const [cursoIdActual, setCursoIdActual] = useState(null);
  const [horarios, setHorarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const loadHorarios = useCallback(async (cursoId) => {
    if (!cursoId) {
      setCursoIdActual(null);
      setHorarios([]);
      return;
    }
    setCursoIdActual(cursoId);
    setLoading(true);
    try {
      const data = await listarHorariosPorCurso(cursoId);
      setHorarios((Array.isArray(data) ? data : []).map(normalizeHorario));
      setError(null);
    } catch (err) {
      console.error(err);
      const msg = err.message || "No se pudieron cargar los horarios.";
      setError(msg);
      showToast({ type: "danger", message: msg });
    } finally {
      setLoading(false);
    }
  }, []);

  const upsertLocal = useCallback((horario) => {
    if (!horario?.id) return;
    setHorarios((prev) => {
      const exists = prev.some((h) => String(h.id) === String(horario.id));
      if (exists) {
        return prev.map((h) => (String(h.id) === String(horario.id) ? normalizeHorario(horario) : h));
      }
      return [normalizeHorario(horario), ...prev];
    });
  }, []);

  const handleCrear = useCallback(
    async (payload) => {
      if (!cursoIdActual) {
        showToast({ type: "warning", message: "Primero guarda el curso para asociar horarios." });
        return;
      }
      const body = { ...payload, curso_id: cursoIdActual };
      setSaving(true);
      try {
        const created = await crearHorario(body);
        const horario = created?.horario ?? created;
        upsertLocal({ ...payload, ...horario, curso_id: cursoIdActual });
        showToast({ type: "success", message: "Horario creado correctamente." });
      } catch (err) {
        console.error(err);
        showToast({ type: "danger", message: err.message || "No se pudo crear el horario." });
      } finally {
        setSaving(false);
      }
    },
    [cursoIdActual, upsertLocal]
  );

  const handleActualizar = useCallback(
    async (id, payload) => {
      if (!id) return;
      setSaving(true);
      try {
        const updated = await actualizarHorario(id, { ...payload, curso_id: cursoIdActual });
        const horario = updated?.horario ?? updated;
        upsertLocal({ ...payload, ...horario, id });
        showToast({ type: "success", message: "Horario actualizado correctamente." });
      } catch (err) {
        console.error(err);
        showToast({ type: "danger", message: err.message || "No se pudo actualizar el horario." });
      } finally {
        setSaving(false);
      }
    },
    [cursoIdActual, upsertLocal]
  );

  const handleEliminar = useCallback(
    async (id) => {
      if (!id) return;
      setDeleting(true);
      try {
        await eliminarHorario(id);
        setHorarios((prev) => prev.filter((h) => String(h.id) !== String(id)));
        showToast({ type: "success", message: "Horario eliminado correctamente." });
      } catch (err) {
        console.error(err);
        showToast({ type: "danger", message: err.message || "No se pudo eliminar el horario." });
      } finally {
        setDeleting(false);
      }
    },
    []
  );

  return {
    cursoIdActual,
    horarios,
    loading,
    saving,
    deleting,
    error,
    loadHorarios,
    crearHorario: handleCrear,
    actualizarHorario: handleActualizar,
    eliminarHorario: handleEliminar,
  };
}
